import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import "./travel.css";
import slideSummer from "../pages/homepage/fotok/utazas1.webp";
import slideWinter from "../pages/homepage/fotok/utazas2.jpeg";
import slideBus from "../pages/homepage/fotok/utazas3.jpg";

const DEFAULT_HERO_SLIDES = [
  { id: "nyar", image_url: slideSummer, title: "Nyári pihenés" },
  { id: "tel", image_url: slideWinter, title: "Téli kaland" },
  { id: "busz", image_url: slideBus, title: "Buszos utazás" },
];

// A három kezdőlapi kategória. A "plane" kizárólagos: ha egy utazás repülős,
// akkor csak itt jelenik meg, az egy/több napos listákból kimarad.
const TRAVEL_TYPES = ["one", "multi", "plane"];

const TYPE_META = {
  one: {
    title: "Egynapos utak",
    desc: "Szökj ki a hétköznapokból, és élj át új élményeket velünk!",
    switchLabel: "az egynapos utakra",
  },
  multi: {
    title: "Többnapos utak",
    desc: "Indulj velünk hosszabb kalandokra, és gyűjts felejthetetlen emlékeket!",
    switchLabel: "a többnapos utakra",
  },
  plane: {
    title: "Repülős utak",
    desc: "Nyiss új távlatokat, és fedezd fel a világot velünk repülővel is!",
    switchLabel: "a repülős utakra",
  },
};

const WHY_ITEMS = [
  { id: "bus", text: "Kényelmes buszos utazás" },
  { id: "program", text: "Gondosan összeállított programok" },
  { id: "hangulat", text: "Jó hangulat" },
];

export function TravelList({ onLoaded }) {
  const [travels, setTravels] = useState([]);
  const [sliderSlides, setSliderSlides] = useState([]);
  const [travelsLoading, setTravelsLoading] = useState(true);
  const [sliderLoading, setSliderLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const readyReportedRef = useRef(false);

  const BASE_URL = process.env.REACT_APP_API_URL || "";
  // UI módok:
  // home = csak a kategória gombok (Egynapos / Többnapos / Repülős)
  // list = listázás
  const [mode, setMode] = useState("home"); // "home" | "list"

  // Kategória: one | multi | plane
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFromUrl = searchParams.get("type"); // "one" | "multi" | "plane" | null
  const [activeType, setActiveType] = useState(
    TRAVEL_TYPES.includes(typeFromUrl) ? typeFromUrl : "one"
  );

  // Biztonságos útvonal összefűzés
  const safeJoin = (base, path) => {
    if (!base) return path.startsWith("/") ? path : `/${path}`;
    const b = base.endsWith("/") ? base.slice(0, -1) : base;
    const p = path.startsWith("/") ? path : `/${path}`;
    return `${b}${p}`;
  };

  useEffect(() => {
    async function fetchTravels() {
      try {
        const endpoint = safeJoin(BASE_URL, "/api/utazasok");
        const response = await fetch(endpoint, { headers: { Accept: "application/json" } });
        if (!response.ok) throw new Error("Nem sikerült lekérni az utazásokat.");
        const data = await response.json();
        setTravels(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || "Ismeretlen hiba");
      } finally {
        setTravelsLoading(false);
      }
    }
    fetchTravels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchSliderSlides() {
      try {
        const endpoint = safeJoin(BASE_URL, "/api/slider-slides");
        const response = await fetch(endpoint, { headers: { Accept: "application/json" } });
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (!Array.isArray(data)) {
          return;
        }
        const normalized = data
          .filter((item) => item && item.image_url)
          .map((item) => ({
            id: item.id,
            image_url: item.image_url,
            title: item.title || "Utazás",
          }));
        setSliderSlides(normalized);
      } catch (err) {
        console.error("Hiba a slider képek lekérdezésekor:", err);
      } finally {
        setSliderLoading(false);
      }
    }
    fetchSliderSlides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = travelsLoading || sliderLoading;

  useEffect(() => {
    if (!loading && onLoaded && !readyReportedRef.current) {
      readyReportedRef.current = true;
      onLoaded();
    }
  }, [loading, onLoaded]);

  const slidesSource = sliderSlides.length ? sliderSlides : DEFAULT_HERO_SLIDES;
  const totalSlides = slidesSource.length;
  const activeSlide = totalSlides ? slidesSource[currentSlide % totalSlides] : null;

  // ha URL-ből jön type, szinkronizáljuk
  useEffect(() => {
    if (TRAVEL_TYPES.includes(typeFromUrl)) {
      setActiveType(typeFromUrl);
      setMode("list"); // ha direkt linkkel jön, rögtön listába dobjuk
      scrollToTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFromUrl]);

  useEffect(() => {
    if (!totalSlides) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 6000);
    return () => clearInterval(timer);
  }, [totalSlides]);

  // --- Segédek a típus meghatározásához ---
  const toDate = (v) => (v ? new Date(v) : null);

  const parseDaysFromText = (txt = "") => {
    const s = String(txt).toLowerCase().trim();
    const m1 = s.match(/(\d+)\s*nap/iu);
    if (m1) return Number(m1[1]);
    if (/\d.*[-–].*\d/.test(s)) return 2;
    return 1;
  };

  const tripDays = (t) => {
    if (t.durationDays != null) {
      const n = Number(t.durationDays);
      if (Number.isFinite(n) && n > 0) return n;
    }
    if (t.startDate || t.endDate) {
      const a = toDate(t.startDate);
      const b = toDate(t.endDate);
      if (a && b && !isNaN(a) && !isNaN(b)) {
        const ms = Math.abs(b - a);
        const days = Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
        if (days >= 1) return days;
      }
    }
    if (t.date) return parseDaysFromText(t.date);
    if (t.title) return parseDaysFromText(t.title) || 1;
    return 1;
  };

  // A repülős jelölő az adminban állítható, a dátumból nem következtethető ki
  const isPlane = (t) => t.repulos === true;
  const isOneDay = (t) => !isPlane(t) && tripDays(t) <= 1;
  const isMultiDay = (t) => !isPlane(t) && tripDays(t) > 1;

  const travelsByType = useMemo(
    () => ({
      one: travels.filter(isOneDay),
      multi: travels.filter(isMultiDay),
      plane: travels.filter(isPlane),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [travels]
  );

  const listToRender = travelsByType[activeType] || [];

  const scrollToTop = (behavior = "auto") => {
    window.scrollTo({ top: 0, behavior });
  };

  const openList = (type) => {
    setActiveType(type);
    setMode("list");
    setSearchParams({ type }); // URL-be is beírjuk
    scrollToTop();
  };

  // Körbeforgat a három kategórián: egynapos → többnapos → repülős → egynapos
  const nextType = TRAVEL_TYPES[(TRAVEL_TYPES.indexOf(activeType) + 1) % TRAVEL_TYPES.length];

  const switchType = () => {
    openList(nextType);
    // opcionális: görgessünk fel, hogy “érzem a váltást”
    scrollToTop("smooth");
  };

  if (loading) {
    return (
      <div className="loader-container">
        <BeatLoader color="#001662" size={30} loading={loading} aria-label="Loading Spinner" />
      </div>
    );
  }

  if (error) return <div className="error">Hiba: {error}</div>;

  // ======= HOME MÓD: csak 2 opció =======
  if (mode === "home") {
    return (
      <div className="travel-list-homepage">
        <section
          className="travel-hero-slider"
          aria-label="Utazási inspirációs képek"
        >
          <div
            className="travel-hero-slide"
            style={{
              backgroundImage: activeSlide ? `url(${activeSlide.image_url})` : "none",
            }}
          >
            <div className="travel-hero-overlay">
              <div className="travel-hero-text">
                <p className="travel-hero-heading">
                  Mi megszervezzük az utakat,
                </p>
                <p className="travel-hero-subheading">
                  te gyűjtsd az élményeket!
                </p>
              </div>
            </div>
          </div>

          <div className="travel-hero-dots" role="tablist" aria-label="Slider navigáció">
            {slidesSource.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                className={`travel-hero-dot ${index === currentSlide ? "is-active" : ""}`}
                aria-label={`${slide.title} kép megnyitása`}
                aria-pressed={index === currentSlide}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </section>

        <div className="travel-choice-grid">
          {TRAVEL_TYPES.map((type) => (
            <button
              key={type}
              className={`travel-choice-card ${type === "one" ? "is-highlight" : ""}`}
              onClick={() => openList(type)}
            >
              <div className="travel-choice-title">{TYPE_META[type].title}</div>
              <div className="travel-choice-desc">{TYPE_META[type].desc}</div>
            </button>
          ))}
        </div>

        <section className="travel-why">
          <h3 className="travel-why__title">Miért minket?</h3>
          <ul className="travel-why__list">
            {WHY_ITEMS.map((item) => (
              <li key={item.id} className="travel-why__item">
                <span className="travel-why__icon" aria-hidden="true">✓</span>
                <p>{item.text}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>
    );
  }

  // ======= LISTA MÓD =======
  return (
    <div className="travel-list-homepage">
      <div className="travel-list-header">
        <h2 className="travel-list__title">{TYPE_META[activeType].title}</h2>
        <div className="travel-back-home-div">
          <button className="travel-back-home" onClick={() => { setMode("home"); setSearchParams({}); }}>
            ← Vissza
          </button>
        </div>
      </div>

      <div
        className="travel-container"
        role="region"
        aria-label={TYPE_META[activeType].title}
      >
        {listToRender.length === 0 ? (
          <div className="travel-empty">Jelenleg nincs megjeleníthető utazás ebben a kategóriában.</div>
        ) : (
          listToRender.map((t) => (
            <div key={t.id} className="travel-card">
              <Link to={`/travel/${t.id}`} state={{ title: t.title }}>
                {t.image && <img src={t.image} alt={t.title || "Utazás"} className="travel-card__image" />}
                {t.date && <div className="travel-card__dates">{t.date}</div>}
                <div className="travel-card__title">{t.title || `Utazás #${t.id}`}</div>
              </Link>
            </div>
          ))
        )}
      </div>

      <div className="travel-switch-footer">
        <button className="travel-switch-btn" onClick={switchType}>
          Ugrás {TYPE_META[nextType].switchLabel} →
        </button>
      </div>
    </div>
  );
}
